import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAnalysisStore } from '@/store/analysisStore'
import { useAuthStore } from '@/store/authStore'

// Decorative sparkle with animation
const Sparkle = ({
  className,
  size = 24,
  color = '#FF8C33',
  delay = 0
}: {
  className?: string
  size?: number
  color?: string
  delay?: number
}) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    initial={{ scale: 0, opacity: 0, rotate: -30 }}
    animate={{
      scale: [0, 1.3, 1],
      opacity: [0, 1, 0.8],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 0.6,
      delay: delay,
      ease: "easeOut"
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

// Confetti piece component
const ConfettiPiece = ({
  delay,
  x,
  color
}: {
  delay: number
  x: number
  color: string
}) => (
  <motion.div
    className="absolute top-0 w-3 h-3 rounded-sm"
    style={{
      left: `${x}%`,
      backgroundColor: color,
    }}
    initial={{ y: -20, opacity: 0, rotate: 0 }}
    animate={{
      y: ['0vh', '100vh'],
      opacity: [0, 1, 1, 0],
      rotate: [0, 360, 720],
      x: [0, Math.random() * 40 - 20, Math.random() * 60 - 30]
    }}
    transition={{
      duration: 3 + Math.random() * 2,
      delay: delay,
      ease: "easeIn"
    }}
  />
)

// Animated waving hand
const WavingHand = () => (
  <motion.span
    className="inline-block text-[48px]"
    animate={{ rotate: [0, 14, -8, 14, -4, 10, 0] }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      repeatDelay: 1
    }}
    style={{ transformOrigin: '70% 70%' }}
  >
    👋
  </motion.span>
)

// Polaroid frame bursting from center
const BurstingPolaroid = ({
  src,
  size = 60,
  delay = 0,
  targetX,
  targetY,
  rotate = 0
}: {
  src: string
  size?: number
  delay?: number
  targetX: number
  targetY: number
  rotate?: number
}) => (
  <motion.div
    className="absolute"
    style={{
      width: size + 20,
      left: '50%',
      top: '50%',
      marginLeft: -(size + 20) / 2,
      marginTop: -(size + 30) / 2,
    }}
    initial={{
      opacity: 0,
      scale: 0,
      x: 0,
      y: 0,
      rotate: 0
    }}
    animate={{
      opacity: [0, 1, 1, 1],
      scale: [0, 1.2, 1],
      x: targetX,
      y: targetY,
      rotate: [0, rotate * 1.5, rotate],
    }}
    transition={{
      duration: 0.8,
      delay: delay,
      ease: [0.34, 1.56, 0.64, 1], // Spring-like easing
    }}
  >
    {/* Gentle floating after burst */}
    <motion.div
      animate={{
        y: [0, -8, 0, -5, 0],
        rotate: [0, 3, -2, 2, 0]
      }}
      transition={{
        duration: 4,
        delay: delay + 0.8,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {/* Polaroid frame */}
      <div
        className="bg-white rounded-[3px] p-[5px] pb-[16px] shadow-lg"
        style={{
          boxShadow: '0 4px 15px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)'
        }}
      >
        {/* Photo with border */}
        <div className="bg-[#f5f5f5] p-[3px] rounded-[2px]">
          <img
            src={src}
            alt=""
            className="w-full h-auto rounded-[1px]"
            style={{ aspectRatio: '1/1', objectFit: 'cover' }}
          />
        </div>
      </div>
    </motion.div>
  </motion.div>
)

// Confetti colors matching brand
const CONFETTI_COLORS = ['#FF8C33', '#46C0C6', '#E8A87C', '#7EC8E3', '#4CD964', '#FFB8B8']

// Product images for floating animation - 30 unique products
const PRODUCT_IMAGES = [
  '/image/polaroid-01.jpg',
  '/image/polaroid-02.jpg',
  '/image/polaroid-03.jpg',
  '/image/polaroid-04.jpg',
  '/image/polaroid-05.jpg',
  '/image/polaroid-06.jpg',
  '/image/polaroid-07.jpg',
  '/image/polaroid-08.jpg',
  '/image/polaroid-09.jpg',
  '/image/polaroid-10.jpg',
  '/image/polaroid-11.jpg',
  '/image/polaroid-12.jpg',
  '/image/polaroid-13.jpg',
  '/image/polaroid-14.jpg',
  '/image/polaroid-15.jpg',
  '/image/polaroid-16.jpg',
  '/image/polaroid-17.jpg',
  '/image/polaroid-18.jpg',
  '/image/polaroid-19.jpg',
  '/image/polaroid-20.jpg',
  '/image/polaroid-21.jpg',
  '/image/polaroid-22.jpg',
  '/image/polaroid-23.jpg',
  '/image/polaroid-24.jpg',
  '/image/polaroid-25.jpg',
  '/image/polaroid-26.jpg',
  '/image/polaroid-27.jpg',
  '/image/polaroid-28.jpg',
  '/image/polaroid-29.jpg',
  '/image/polaroid-30.jpg',
]

const COUNTDOWN_SECONDS = 5

export function LoginWelcomePage() {
  const navigate = useNavigate()
  const { analysisResults } = useAnalysisStore()
  const { isAuthenticated, hasPaid, demoMode } = useAuthStore()
  const hasRedirected = useRef(false)
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS)

  // Route guard
  useEffect(() => {
    if (hasRedirected.current) return

    if (!analysisResults) {
      hasRedirected.current = true
      navigate('/survey', { replace: true })
      return
    }

    // If already paid, go to report
    if (hasPaid || demoMode) {
      hasRedirected.current = true
      navigate('/report', { replace: true })
      return
    }

    // If not authenticated, go back to login
    if (!isAuthenticated) {
      hasRedirected.current = true
      navigate('/login', { replace: true })
    }
  }, [analysisResults, isAuthenticated, hasPaid, demoMode, navigate])

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          navigate('/payment')
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [navigate])

  const handleSkip = () => {
    navigate('/payment')
  }

  // Generate confetti pieces
  const confettiPieces = Array.from({ length: 25 }, (_, i) => ({
    id: i,
    delay: Math.random() * 0.3,
    x: Math.random() * 100,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
  }))

  return (
    <div className="min-h-dvh flex flex-col overflow-hidden relative">
      {/* CSS for animated gradient */}
      <style>{`
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animated-gradient-bg {
          background: linear-gradient(-45deg, #FFFEF1, #E8F8EB, #F0FAFA, #FFFEF1);
          background-size: 400% 400%;
          animation: gradient-shift 8s ease infinite;
        }
      `}</style>

      {/* Animated gradient background */}
      <div className="absolute inset-0 animated-gradient-bg" />

      {/* Confetti */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {confettiPieces.map((piece) => (
          <ConfettiPiece
            key={piece.id}
            delay={piece.delay}
            x={piece.x}
            color={piece.color}
          />
        ))}
      </div>

      {/* Bursting polaroid product images from center - 30 unique products, avoiding center text */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
        {/* Top far corners - small */}
        <BurstingPolaroid src={PRODUCT_IMAGES[0]} size={28} delay={0.02} targetX={-145} targetY={-340} rotate={-25} />
        <BurstingPolaroid src={PRODUCT_IMAGES[1]} size={32} delay={0.04} targetX={150} targetY={-335} rotate={22} />

        {/* Top far - burst way up - medium */}
        <BurstingPolaroid src={PRODUCT_IMAGES[2]} size={55} delay={0.06} targetX={-80} targetY={-320} rotate={-18} />
        <BurstingPolaroid src={PRODUCT_IMAGES[3]} size={38} delay={0.08} targetX={85} targetY={-325} rotate={15} />

        {/* Top row - burst upward and outward - large */}
        <BurstingPolaroid src={PRODUCT_IMAGES[4]} size={65} delay={0.1} targetX={-155} targetY={-260} rotate={-12} />
        <BurstingPolaroid src={PRODUCT_IMAGES[5]} size={48} delay={0.12} targetX={0} targetY={-290} rotate={5} />
        <BurstingPolaroid src={PRODUCT_IMAGES[6]} size={58} delay={0.14} targetX={160} targetY={-255} rotate={10} />

        {/* Upper sides - pushed to edges */}
        <BurstingPolaroid src={PRODUCT_IMAGES[7]} size={35} delay={0.16} targetX={-175} targetY={-180} rotate={-8} />
        <BurstingPolaroid src={PRODUCT_IMAGES[8]} size={70} delay={0.18} targetX={-160} targetY={-100} rotate={14} />
        <BurstingPolaroid src={PRODUCT_IMAGES[9]} size={42} delay={0.2} targetX={165} targetY={-175} rotate={-10} />
        <BurstingPolaroid src={PRODUCT_IMAGES[10]} size={30} delay={0.22} targetX={175} targetY={-95} rotate={16} />

        {/* Middle row - burst sideways only - large */}
        <BurstingPolaroid src={PRODUCT_IMAGES[11]} size={62} delay={0.24} targetX={-180} targetY={-20} rotate={-6} />
        <BurstingPolaroid src={PRODUCT_IMAGES[12]} size={55} delay={0.26} targetX={185} targetY={-15} rotate={12} />

        {/* Center sides - small, pushed far out */}
        <BurstingPolaroid src={PRODUCT_IMAGES[13]} size={28} delay={0.28} targetX={-175} targetY={60} rotate={8} />
        <BurstingPolaroid src={PRODUCT_IMAGES[14]} size={34} delay={0.3} targetX={180} targetY={55} rotate={-14} />

        {/* Lower sides - pushed to edges */}
        <BurstingPolaroid src={PRODUCT_IMAGES[15]} size={50} delay={0.32} targetX={-170} targetY={140} rotate={10} />
        <BurstingPolaroid src={PRODUCT_IMAGES[16]} size={72} delay={0.34} targetX={-160} targetY={220} rotate={-8} />
        <BurstingPolaroid src={PRODUCT_IMAGES[17]} size={38} delay={0.36} targetX={165} targetY={135} rotate={12} />
        <BurstingPolaroid src={PRODUCT_IMAGES[18]} size={45} delay={0.38} targetX={175} targetY={215} rotate={-15} />

        {/* Bottom row - burst downward and outward - large */}
        <BurstingPolaroid src={PRODUCT_IMAGES[19]} size={60} delay={0.4} targetX={-145} targetY={290} rotate={-10} />
        <BurstingPolaroid src={PRODUCT_IMAGES[20]} size={52} delay={0.42} targetX={0} targetY={320} rotate={6} />
        <BurstingPolaroid src={PRODUCT_IMAGES[21]} size={68} delay={0.44} targetX={150} targetY={285} rotate={-12} />

        {/* Bottom far - burst way down - medium */}
        <BurstingPolaroid src={PRODUCT_IMAGES[22]} size={40} delay={0.46} targetX={-85} targetY={340} rotate={15} />
        <BurstingPolaroid src={PRODUCT_IMAGES[23]} size={32} delay={0.48} targetX={90} targetY={345} rotate={-18} />

        {/* Bottom far corners - small */}
        <BurstingPolaroid src={PRODUCT_IMAGES[24]} size={26} delay={0.5} targetX={-150} targetY={360} rotate={20} />
        <BurstingPolaroid src={PRODUCT_IMAGES[25]} size={30} delay={0.52} targetX={155} targetY={365} rotate={-22} />

        {/* Extra scattered on edges - varied */}
        <BurstingPolaroid src={PRODUCT_IMAGES[26]} size={24} delay={0.54} targetX={-110} targetY={-300} rotate={-28} />
        <BurstingPolaroid src={PRODUCT_IMAGES[27]} size={58} delay={0.56} targetX={115} targetY={310} rotate={25} />
        <BurstingPolaroid src={PRODUCT_IMAGES[28]} size={44} delay={0.58} targetX={-185} targetY={-50} rotate={18} />
        <BurstingPolaroid src={PRODUCT_IMAGES[29]} size={36} delay={0.6} targetX={190} targetY={90} rotate={-20} />
      </div>

      {/* Decorative sparkles */}
      <Sparkle className="absolute top-16 left-6 z-10" size={28} color="#FF8C33" delay={0.2} />
      <Sparkle className="absolute top-24 right-8 z-10" size={24} color="#46C0C6" delay={0.4} />
      <Sparkle className="absolute top-40 left-12 z-10" size={18} color="#4CD964" delay={0.6} />
      <Sparkle className="absolute bottom-40 right-10 z-10" size={26} color="#7EC8E3" delay={0.3} />
      <Sparkle className="absolute bottom-56 left-8 z-10" size={20} color="#E8A87C" delay={0.5} />
      <Sparkle className="absolute top-48 right-16 z-10" size={16} color="#FFB8B8" delay={0.7} />

      {/* Header */}
      <header className="relative z-10 py-5 px-4">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[18px] text-center"
          style={{ fontFamily: "'Oleo Script', cursive" }}
        >
          <span className="text-[#688192]">K-Beauty</span>
          <span className="text-[#46C0C6]"> AI</span>
        </motion.h1>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="max-w-sm w-full text-center">
          {/* Waving Hand + Welcome */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mb-4"
          >
            <WavingHand />
          </motion.div>

          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <h1
              className="text-[28px] text-black mb-2"
              style={{ fontFamily: "'Anybody', sans-serif", fontWeight: 600 }}
            >
              <span className="text-[#4CD964]">Welcome!</span>
            </h1>
            <h2
              className="text-[20px] text-black mb-4"
              style={{ fontFamily: "'Anybody', sans-serif", fontWeight: 500 }}
            >
              Let's unlock your
              <br />
              <span className="text-[#46C0C6]">full skin report</span>
            </h2>
          </motion.div>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="text-[#688192] mb-8 leading-relaxed text-[15px]"
          >
            Your personalized K-beauty routine
            <br />
            is just one step away!
          </motion.p>

        </div>
      </div>

      {/* Bottom: Skip button and countdown */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="relative z-10 pb-8 pt-4 text-center"
      >
        <button
          onClick={handleSkip}
          className="text-[#688192] underline text-[14px] mb-2 hover:text-[#46C0C6] transition-colors"
        >
          skip
        </button>
        <p className="text-[#A0A0A0] text-[13px]">
          {countdown} second{countdown !== 1 ? 's' : ''} left
        </p>
      </motion.footer>
    </div>
  )
}
